import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "../../components/VB";
import RupiahInput from "../../components/RupiahInput";
import ContractDocument from "../../components/ContractDocument";
import { toast } from "sonner";
import { Plus, Trash2, Send, Eye, Printer, RefreshCw, XCircle, FileText } from "lucide-react";

const emptyForm = {
  employee_id: "", jenis_kontrak: "PKWT", nomor_kontrak: "",
  tanggal_mulai: "", tanggal_selesai: "", durasi_text: "Tiga (3) Bulan",
  posisi: "", divisi: "",
  gaji_pokok: 0, gaji_terbilang: "", tunjangan_info: "",
  jam_kerja: "08:00 - 16:00 WIB", hari_kerja: "Senin s/d Sabtu",
  hak_cuti_tahunan: 12, probation_months: 3,
  isi_tugas: "", isi_pasal_tambahan: "",
  ceo_nama: "UBAIDILLAH MUFNIL AFIF",
  ceo_jabatan: "CEO PT FIVEHOSTER KARYA BANGSA",
  ceo_alamat: "Jl. Awikoen Madya Mulya 29A Kebomas Gresik",
  tempat_pembuatan: "Malang",
  parent_contract_id: ""
};

const StatusBadge = ({ status }) => {
  const map = {
    "Draft": "vb-badge-pending",
    "Menunggu Tanda Tangan": "vb-badge-pending",
    "Aktif": "vb-badge-approved",
    "Diperpanjang": "vb-badge-izin",
    "Terminated": "vb-badge-rejected",
    "Expired": "vb-badge-terlambat",
  };
  return <span className={`vb-badge ${map[status] || "vb-badge-pending"}`}>{status}</span>;
};

export default function Kontrak() {
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [divs, setDivs] = useState([]);
  const [jabs, setJabs] = useState([]);
  const [tugasTpl, setTugasTpl] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [renewalParent, setRenewalParent] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [del, setDel] = useState(null);
  const [viewContract, setViewContract] = useState(null);
  const [termTarget, setTermTarget] = useState(null);
  const [termForm, setTermForm] = useState({ tanggal_berakhir: "", alasan: "" });
  const [gaji, setGaji] = useState([]); // Gaji list untuk employee
  const [tunjangan, setTunjangan] = useState([]); // Tunjangan list untuk employee
  const [selectedTunjangan, setSelectedTunjangan] = useState([]); // Tunjangan yg dipilih untuk kontrak
  const [sendTarget, setSendTarget] = useState(null);

  // Tugas template management
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ nama_template: "", posisi: "", isi_tugas: "" });
  const [tplEditId, setTplEditId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, e, d, j, t] = await Promise.all([
        api.get("/contracts"), api.get("/employees"),
        api.get("/divisi"), api.get("/jabatan"), api.get("/tugas-template"),
      ]);
      setItems(c.data); setEmps(e.data); setDivs(d.data); setJabs(j.data); setTugasTpl(t.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const applyTugasTemplate = (tplId) => {
    const t = tugasTpl.find((x) => x.id === tplId);
    if (t) setForm((f) => ({ ...f, isi_tugas: t.isi_tugas }));
  };

  const saveTpl = async () => {
    try {
      if (tplEditId) await api.put(`/tugas-template/${tplEditId}`, tplForm);
      else await api.post("/tugas-template", tplForm);
      toast.success("Template tersimpan"); setTplOpen(false);
      setTplForm({ nama_template: "", posisi: "", isi_tugas: "" }); setTplEditId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delTpl = async (id) => {
    if (!window.confirm("Hapus template ini?")) return;
    await api.delete(`/tugas-template/${id}`);
    toast.success("Template dihapus");
    load();
  };

  const fetchGajiTunjangan = async (empId) => {
    try {
      if (!empId) {
        setGaji([]);
        setTunjangan([]);
        setSelectedTunjangan([]);
        return;
      }
      const res = await api.get(`/employees/${empId}/gaji-tunjangan`);
      setGaji(res.data.gaji || []);
      setTunjangan(res.data.tunjangan || []);
      setSelectedTunjangan([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openCreate = () => {
    setForm({ ...emptyForm, tanggal_mulai: new Date().toISOString().slice(0, 10) });
    setEditId(null); setRenewalParent(null); 
    setGaji([]);
    setTunjangan([]);
    setSelectedTunjangan([]);
    setOpen(true);
  };

  const openEdit = (it) => { 
    setForm({ ...emptyForm, ...it }); 
    setEditId(it.id); 
    setRenewalParent(null); 
    fetchGajiTunjangan(it.employee_id);
    setOpen(true); 
  };

  const openRenewal = (parent) => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      ...emptyForm,
      employee_id: parent.employee_id,
      jenis_kontrak: parent.jenis_kontrak,
      posisi: parent.posisi, divisi: parent.divisi,
      gaji_pokok: parent.gaji_pokok,
      tunjangan_info: parent.tunjangan_info,
      jam_kerja: parent.jam_kerja, hari_kerja: parent.hari_kerja,
      hak_cuti_tahunan: parent.hak_cuti_tahunan,
      tanggal_mulai: today,
      probation_months: 0,
      parent_contract_id: parent.id,
    });
    setEditId(null); setRenewalParent(parent); 
    fetchGajiTunjangan(parent.employee_id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Prepare gaji_detail and tunjangan_detail
      const gajiDetail = gaji.length > 0 ? gaji[0] : null;
      const tunjanganDetail = selectedTunjangan.length > 0 
        ? tunjangan.filter(t => selectedTunjangan.includes(t.id))
        : [];

      const payload = { 
        ...form, 
        gaji_pokok: parseFloat(form.gaji_pokok), 
        hak_cuti_tahunan: parseInt(form.hak_cuti_tahunan), 
        probation_months: parseInt(form.probation_months || 0),
        gaji_detail: gajiDetail,
        tunjangan_detail: tunjanganDetail
      };
      if (renewalParent) {
        await api.post(`/contracts/${renewalParent.id}/renewal`, payload);
        toast.success("Kontrak perpanjangan dibuat");
      } else if (editId) {
        await api.put(`/contracts/${editId}`, payload);
        toast.success("Kontrak diperbarui");
      } else {
        await api.post("/contracts", payload);
        toast.success("Kontrak dibuat (Draft)");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendContract = async () => {
    try {
      await api.post(`/contracts/${sendTarget.id}/send`);
      toast.success("Kontrak dikirim ke karyawan untuk ditandatangani");
      setSendTarget(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/contracts/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const terminate = async () => {
    try {
      await api.post(`/contracts/${termTarget.id}/terminate`, termForm);
      toast.success("Kontrak diberhentikan");
      setTermTarget(null); setTermForm({ tanggal_berakhir: "", alasan: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const viewDetail = async (id) => {
    try {
      const { data } = await api.get(`/contracts/${id}`);
      setViewContract(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (viewContract) {
    return (
      <div>
        <div className="no-print" style={{ marginBottom: 8, display: "flex", gap: 6 }}>
          <button className="vb-btn" onClick={() => setViewContract(null)}>← Kembali</button>
          <button className="vb-btn-primary" onClick={() => setTimeout(() => window.print(), 100)}>
            <Printer className="w-3 h-3 inline mr-1" />Cetak Kontrak
          </button>
        </div>
        <ContractDocument data={viewContract} />
      </div>
    );
  }

  return (
    <Window
      title="Management Kontrak Karyawan (PKWT/PKWTT)"
      actions={<>
        <button className="vb-btn" onClick={() => { setTplForm({ nama_template: "", posisi: "", isi_tugas: "" }); setTplEditId(null); setTplOpen(true); }} data-testid="manage-tpl"><FileText className="w-3 h-3 inline mr-1" />Template Tugas</button>
        <button className="vb-btn" onClick={openCreate} data-testid="add-kontrak"><Plus className="w-3 h-3 inline mr-1" />Buat Kontrak Baru</button>
      </>}
    >
      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada kontrak" /> : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead>
              <tr>
                <th>No. Kontrak</th><th>Karyawan</th><th>Jenis</th>
                <th>Posisi</th><th>Periode</th><th>Status</th><th>TTD</th><th style={{ width: 280 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{it.nomor_kontrak}</td>
                  <td><strong>{it.employee_name}</strong> <span style={{ fontSize: 10, color: "#666" }}>({it.no_karyawan})</span></td>
                  <td>{it.jenis_kontrak}{it.is_renewal && <span style={{ fontSize: 10, color: "#003399", marginLeft: 4 }}>(perpanjangan)</span>}</td>
                  <td>{it.posisi}</td>
                  <td style={{ fontSize: 11 }}>
                    {it.tanggal_mulai}<br />
                    {it.tanggal_selesai ? `s/d ${it.tanggal_selesai}` : <em>(tidak terbatas)</em>}
                  </td>
                  <td><StatusBadge status={it.status} /></td>
                  <td>{it.signature_data ? <span style={{ color: "#4f8a10" }}>✓ Sudah</span> : <span style={{ color: "#9F6000" }}>Belum</span>}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 3 }} onClick={() => viewDetail(it.id)} data-testid={`view-kontrak-${it.id}`}>
                      <Eye className="w-3 h-3 inline" />
                    </button>
                    {it.status === "Draft" && (
                      <>
                        <button className="vb-btn-primary" style={{ padding: "2px 6px", marginRight: 3 }} onClick={() => setSendTarget(it)} data-testid={`send-${it.id}`}>
                          <Send className="w-3 h-3 inline mr-1" />Kirim
                        </button>
                        <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 3 }} onClick={() => openEdit(it)}>Edit</button>
                        <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}><Trash2 className="w-3 h-3" /></button>
                      </>
                    )}
                    {(it.status === "Aktif" || it.status === "Diperpanjang") && (
                      <>
                        <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 3 }} onClick={() => openRenewal(it)} data-testid={`renew-${it.id}`}>
                          <RefreshCw className="w-3 h-3 inline mr-1" />Perpanjang
                        </button>
                        <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => { setTermTarget(it); setTermForm({ tanggal_berakhir: new Date().toISOString().slice(0, 10), alasan: "" }); }} data-testid={`term-${it.id}`}>
                          <XCircle className="w-3 h-3 inline" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog
        open={open} onClose={() => setOpen(false)}
        title={renewalParent ? `Perpanjangan Kontrak - ${renewalParent.employee_name}` : (editId ? "Edit Kontrak" : "Buat Kontrak Baru")}
        width={680}
        footer={<>
          <button className="vb-btn" onClick={() => setOpen(false)}>Batal</button>
          <button className="vb-btn-primary" onClick={submit} data-testid="kontrak-save">Simpan</button>
        </>}
      >
        <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Karyawan*</label>
            <select 
              className="vb-select" 
              value={form.employee_id} 
              onChange={(e) => {
                setForm({ ...form, employee_id: e.target.value });
                fetchGajiTunjangan(e.target.value);
              }} 
              required 
              disabled={!!renewalParent} 
              data-testid="kontrak-employee"
            >
              <option value="">- pilih karyawan -</option>
              {emps.map((e) => <option key={e.id} value={e.id}>{e.nama_lengkap} ({e.no_karyawan})</option>)}
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Jenis Kontrak*</label>
            <select className="vb-select" value={form.jenis_kontrak} onChange={(e) => setForm({ ...form, jenis_kontrak: e.target.value })} data-testid="kontrak-jenis">
              <option value="PKWT">PKWT (Waktu Tertentu)</option>
              <option value="PKWTT">PKWTT (Waktu Tidak Tertentu)</option>
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">No. Kontrak (auto jika kosong)</label>
            <input className="vb-input" value={form.nomor_kontrak} onChange={(e) => setForm({ ...form, nomor_kontrak: e.target.value })} />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Tanggal Mulai*</label>
            <input type="date" className="vb-input" value={form.tanggal_mulai} onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })} required data-testid="kontrak-mulai" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Tanggal Selesai{form.jenis_kontrak === "PKWT" ? "*" : " (kosongkan untuk PKWTT)"}</label>
            <input type="date" className="vb-input" value={form.tanggal_selesai} onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })} required={form.jenis_kontrak === "PKWT"} data-testid="kontrak-selesai" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Posisi / Jabatan*</label>
            <input className="vb-input" value={form.posisi} onChange={(e) => setForm({ ...form, posisi: e.target.value })} required list="jab-list" data-testid="kontrak-posisi" />
            <datalist id="jab-list">{jabs.map((j) => <option key={j.id} value={j.nama} />)}</datalist>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Divisi*</label>
            <input className="vb-input" value={form.divisi} onChange={(e) => setForm({ ...form, divisi: e.target.value })} required list="div-list" data-testid="kontrak-divisi" />
            <datalist id="div-list">{divs.map((d) => <option key={d.id} value={d.nama} />)}</datalist>
          </div>

          {/* Data Gaji & Tunjangan */}
          {form.employee_id && (
            <>
              <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
                <label className="vb-label" style={{ fontWeight: 700, color: "#003399 " }}>📊 Data Gaji & Tunjangan Karyawan</label>
              </div>
              
              {gaji.length > 0 ? (
                <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
                  <label className="vb-label">✓ Data Gaji Aktif (tersedia)</label>
                  <div style={{ fontSize: 12, color: "#4f8a10", padding: "6px 8px", background: "#f1f8f4", border: "1px solid #c6e3d8", borderRadius: 3 }}>
                    {gaji.map(g => (
                      <div key={g.id} style={{ marginBottom: 4 }}>
                        <strong>{g.jenis}</strong>: Rp {g.nominal?.toLocaleString("id-ID") || "0"}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
                  <label className="vb-label" style={{ color: "#d8000c" }}>⚠️ Data Gaji Tidak Ada (Wajib Dibuat)</label>
                  <div style={{ fontSize: 11, color: "#d8000c", padding: "6px 8px", background: "#ffbaba", border: "1px solid #d8000c", borderRadius: 3 }}>
                    Silahkan masukkan data Gaji di menu <strong>Management Gaji</strong> sebelum membuat kontrak
                  </div>
                </div>
              )}

              {tunjangan.length > 0 && (
                <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
                  <label className="vb-label">Tunjangan (Pilih untuk ditampilkan di kontrak)</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {tunjangan.map(t => (
                      <label key={t.id} style={{ display: "flex", alignItems: "center", fontSize: 12, padding: "6px", border: "1px solid #ccc", borderRadius: 3, cursor: "pointer" }}>
                        <input 
                          type="checkbox" 
                          checked={selectedTunjangan.includes(t.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTunjangan([...selectedTunjangan, t.id]);
                            } else {
                              setSelectedTunjangan(selectedTunjangan.filter(id => id !== t.id));
                            }
                          }}
                          style={{ marginRight: 6 }}
                        />
                        <span><strong>{t.nama}</strong> ({t.jenis}): Rp {t.nominal?.toLocaleString("id-ID") || "0"}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="vb-form-row">
            <label className="vb-label">Gaji Pokok*</label>
            <RupiahInput value={form.gaji_pokok} onChange={(v) => setForm({ ...form, gaji_pokok: v })} required testId="kontrak-gaji" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Masa Probation (bulan)</label>
            <input type="number" min={0} max={12} className="vb-input" value={form.probation_months} onChange={(e) => setForm({ ...form, probation_months: e.target.value })} />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Hari Kerja</label>
            <input className="vb-input" value={form.hari_kerja} onChange={(e) => setForm({ ...form, hari_kerja: e.target.value })} />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Jam Kerja</label>
            <input className="vb-input" value={form.jam_kerja} onChange={(e) => setForm({ ...form, jam_kerja: e.target.value })} />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Hak Cuti Tahunan (hari)</label>
            <input type="number" className="vb-input" value={form.hak_cuti_tahunan} onChange={(e) => setForm({ ...form, hak_cuti_tahunan: e.target.value })} />
          </div>
          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Tunjangan / Fasilitas</label>
            <input className="vb-input" value={form.tunjangan_info} onChange={(e) => setForm({ ...form, tunjangan_info: e.target.value })} placeholder="Transport, makan, BPJS, dll" />
          </div>

          {/* PASAL 4 AYAT 2 - TUGAS */}
          <div style={{ gridColumn: "1 / 3", marginTop: 6, paddingTop: 8, borderTop: "1px dashed #828790" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#003399" }}>Pasal 4 Ayat 2 — Tugas &amp; Tanggung Jawab</div>
          </div>
          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Load dari Template (opsional)</label>
            <select className="vb-select" onChange={(e) => e.target.value && applyTugasTemplate(e.target.value)} data-testid="kontrak-tpl">
              <option value="">- pilih template -</option>
              {tugasTpl.map((t) => <option key={t.id} value={t.id}>{t.nama_template} {t.posisi && `(${t.posisi})`}</option>)}
            </select>
          </div>
          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Isi Tugas* (satu baris = satu point, akan otomatis dinomori)</label>
            <textarea rows={8} className="vb-textarea" value={form.isi_tugas} onChange={(e) => setForm({ ...form, isi_tugas: e.target.value })} placeholder={"Contoh:\nMengerjakan launching projek yang diminta oleh client\nMemasang BOT di Telegram\nMembuat postingan sosial media"} data-testid="kontrak-tugas" />
          </div>

          {/* CEO Info */}
          <div style={{ gridColumn: "1 / 3", marginTop: 6, paddingTop: 8, borderTop: "1px dashed #828790" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#003399" }}>Data PIHAK PERTAMA (Pimpinan)</div>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Nama Pimpinan</label>
            <input className="vb-input" value={form.ceo_nama} onChange={(e) => setForm({ ...form, ceo_nama: e.target.value })} />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Jabatan Pimpinan</label>
            <input className="vb-input" value={form.ceo_jabatan} onChange={(e) => setForm({ ...form, ceo_jabatan: e.target.value })} />
          </div>
          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Alamat Pimpinan</label>
            <input className="vb-input" value={form.ceo_alamat} onChange={(e) => setForm({ ...form, ceo_alamat: e.target.value })} />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Tempat Pembuatan Kontrak</label>
            <input className="vb-input" value={form.tempat_pembuatan} onChange={(e) => setForm({ ...form, tempat_pembuatan: e.target.value })} />
          </div>

          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Pasal Tambahan (opsional)</label>
            <textarea rows={3} className="vb-textarea" value={form.isi_pasal_tambahan} onChange={(e) => setForm({ ...form, isi_pasal_tambahan: e.target.value })} placeholder="Ketentuan khusus, kewajiban, kerahasiaan, dll" />
          </div>
        </form>
      </Dialog>

      {/* Send Confirmation */}
      <ConfirmDialog
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        onConfirm={sendContract}
        title="Kirim Kontrak ke Karyawan"
        message={`Kirim kontrak ${sendTarget?.nomor_kontrak} ke ${sendTarget?.employee_name} untuk ditandatangani? Setelah dikirim kontrak tidak bisa diedit.`}
      />

      {/* Terminate Dialog */}
      <Dialog
        open={!!termTarget} onClose={() => setTermTarget(null)} title="Berhentikan Kontrak"
        footer={<>
          <button className="vb-btn" onClick={() => setTermTarget(null)}>Batal</button>
          <button className="vb-btn-danger" onClick={terminate}>Berhentikan</button>
        </>}
      >
        <p style={{ fontSize: 12 }}>Berhentikan kontrak <strong>{termTarget?.nomor_kontrak}</strong>?</p>
        <div className="vb-form-row">
          <label className="vb-label">Tanggal Berakhir*</label>
          <input type="date" className="vb-input" value={termForm.tanggal_berakhir} onChange={(e) => setTermForm({ ...termForm, tanggal_berakhir: e.target.value })} />
        </div>
        <div className="vb-form-row">
          <label className="vb-label">Alasan*</label>
          <textarea rows={3} className="vb-textarea" value={termForm.alasan} onChange={(e) => setTermForm({ ...termForm, alasan: e.target.value })} required />
        </div>
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus kontrak ${del?.nomor_kontrak}?`} />

      {/* Template Tugas Manager */}
      <Dialog
        open={tplOpen} onClose={() => setTplOpen(false)} title="Kelola Template Tugas" width={680}
        footer={<button className="vb-btn" onClick={() => setTplOpen(false)}>Tutup</button>}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
          <input className="vb-input" placeholder="Nama Template" value={tplForm.nama_template} onChange={(e) => setTplForm({ ...tplForm, nama_template: e.target.value })} data-testid="tpl-nama" />
          <input className="vb-input" placeholder="Posisi (opsional)" value={tplForm.posisi} onChange={(e) => setTplForm({ ...tplForm, posisi: e.target.value })} />
        </div>
        <textarea rows={6} className="vb-textarea" placeholder="Daftar tugas (1 baris = 1 point)" value={tplForm.isi_tugas} onChange={(e) => setTplForm({ ...tplForm, isi_tugas: e.target.value })} data-testid="tpl-isi" />
        <div style={{ marginTop: 8, textAlign: "right" }}>
          <button className="vb-btn-primary" onClick={saveTpl} disabled={!tplForm.nama_template || !tplForm.isi_tugas} data-testid="tpl-save">
            {tplEditId ? "Update Template" : "Tambah Template"}
          </button>
        </div>

        <div style={{ marginTop: 16, paddingTop: 10, borderTop: "1px solid #ccc" }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Daftar Template Tersimpan</div>
          {tugasTpl.length === 0 ? <EmptyState message="Belum ada template" /> : (
            <table className="vb-table">
              <thead><tr><th>Nama</th><th>Posisi</th><th>Preview</th><th>Aksi</th></tr></thead>
              <tbody>
                {tugasTpl.map((t) => (
                  <tr key={t.id}>
                    <td>{t.nama_template}</td>
                    <td>{t.posisi || "-"}</td>
                    <td style={{ fontSize: 11, color: "#666", maxWidth: 250, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.isi_tugas.substring(0, 60)}...</td>
                    <td>
                      <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setTplForm({ nama_template: t.nama_template, posisi: t.posisi || "", isi_tugas: t.isi_tugas }); setTplEditId(t.id); }}>Edit</button>
                      <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => delTpl(t.id)}><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Dialog>
    </Window>
  );
}
